import { Injectable, computed, effect, inject, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, map, switchMap, tap, debounceTime, filter, catchError, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Trip } from 'src/app/trip-calculator/models/trip';
import { Project } from 'src/app/admin/models/project.model';
import { ContributionPreviewResponse } from 'src/app/shared/models/contribution.models';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';

// ============================================
// Types
// ============================================

export type PaymentMethod = 'CARD' | 'BANK_TRANSFER';

// URL state for wizard persistence
export interface WizardUrlState {
  step?: number;
  projectId?: string;
  single?: string; // 'true' or 'false' as string from URL
  pct?: string; // number as string from URL
  name?: string;
  msg?: string;
}

export interface CreateContributionDTO {
  tripId: string;
  isSingleTraveler: boolean;
  globalPercentage: number;
  projects: { projectId: string; percentage: number }[];
  certificateRecipientName: string;
  certificateMessage?: string;
}

export interface PreviewContributionDTO {
  tripId: string;
  isSingleTraveler: boolean;
  globalPercentage: number;
  projects: { projectId: string; percentage: number }[];
  vatNumber?: string; // Optional VAT number override for preview
}

export interface ContributionResult {
  id: string;
  status: string;
  totalAmount: number;
  co2Compensated: number;
  certificateRecipientName: string;
  certificateNumber?: string;
  certificateUrl?: string;
}

export interface BankDetails {
  iban: string;
  bic: string;
  accountName: string;
  reference: string;
  amount: number;
}

export interface PaymentInitiateResponse {
  paymentMethod: PaymentMethod;
  checkoutUrl?: string;
  sessionId?: string;
  message?: string;
  bankDetails?: BankDetails;
}

export interface VerifySessionProject {
  id: string;
  nameEN: string;
  nameFR: string;
  nameNL: string;
  shortDescriptionEN: string;
  shortDescriptionFR: string;
  shortDescriptionNL: string;
  mainImageUrl?: string | null;
  pricePerTonne: number;
}

export interface VerifySessionResponse {
  status: string;
  certificateNumber?: string;
  certificateS3Key?: string;
  contribution?: ContributionResult;
  project?: VerifySessionProject | null;
}

// VAT validation types
export type VatValidationMethod = 'vies' | 'format_only' | 'invalid';

export interface VatValidationResult {
  isValid: boolean;
  countryCode: string | null;
  validationMethod: VatValidationMethod;
  companyName?: string;
  isReverseChargeEligible: boolean;
  errorMessage?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ContributeWizardService {
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  private currentUserService = inject(CurrentUserService);
  private baseUrl = `${environment.apiUrl}/contributions`;

  // Trip data
  tripId = signal<string>('');
  trip = signal<Trip | null>(null);

  // Step 1: Project selection
  selectedProject = signal<Project | null>(null);

  // Step 2: Contribution adjustment
  isSingleTraveler = signal<boolean>(false);
  globalPercentage = signal<number>(100);

  // Step 3: Certificate
  certificateRecipientName = signal<string>('');
  certificateMessage = signal<string>('');

  // Result after creation
  contributionResult = signal<ContributionResult | null>(null);

  // Step 4: Payment
  selectedPaymentMethod = signal<PaymentMethod | null>(null);
  bankDetails = signal<BankDetails | null>(null);

  // ============================================
  // Preview API State
  // ============================================
  private previewResponse = signal<ContributionPreviewResponse | null>(null);
  private previewLoading = signal(false);
  private previewError = signal<string | null>(null);
  private previewTrigger$ = new Subject<void>();

  // Exposed preview loading state
  isPreviewLoading = this.previewLoading.asReadonly();

  // VAT breakdown from preview API
  vatBreakdown = computed(() => this.previewResponse()?.vatBreakdown ?? null);
  isReverseCharge = computed(() => this.vatBreakdown()?.isReverseCharge ?? false);
  vatRate = computed(() => this.vatBreakdown()?.vatRate ?? 0.21);

  // ============================================
  // VAT Number Validation State
  // ============================================
  vatNumberInput = signal<string>('');
  private vatValidationResult = signal<VatValidationResult | null>(null);
  private vatValidationLoading = signal(false);
  private vatValidationTrigger$ = new Subject<string>();

  // Exposed VAT validation state
  isVatValidationLoading = this.vatValidationLoading.asReadonly();
  vatValidation = this.vatValidationResult.asReadonly();

  // Computed: Show validated company name if available
  validatedCompanyName = computed(() => this.vatValidationResult()?.companyName ?? null);

  // Computed: Is the entered VAT number valid and eligible for reverse charge?
  isVatNumberValid = computed(() => {
    const result = this.vatValidationResult();
    return result?.isValid ?? false;
  });

  isEligibleForReverseCharge = computed(() => {
    const result = this.vatValidationResult();
    return result?.isReverseChargeEligible ?? false;
  });

  constructor() {
    // Setup debounced preview fetching
    this.previewTrigger$
      .pipe(
        debounceTime(300),
        filter(() => this.canFetchPreview()),
        switchMap(() => this.fetchPreviewInternal()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // Setup debounced VAT validation
    this.vatValidationTrigger$
      .pipe(
        debounceTime(500),
        filter((vatNumber) => vatNumber.length >= 4), // Minimum length for VAT numbers
        switchMap((vatNumber) => this.validateVatInternal(vatNumber)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    // Auto-trigger preview when relevant signals change
    // This effect runs in the service, so it's always active
    effect(() => {
      // Track all signals that affect the preview
      const tripId = this.tripId();
      const project = this.selectedProject();
      const percentage = this.globalPercentage();
      // Read these signals to track changes and trigger effect on change
      this.isSingleTraveler();
      this.vatValidationResult();

      // Trigger preview if we have required data
      if (tripId && project && percentage > 0) {
        this.triggerPreview();
      }
    });
  }

  // Computed: Base CO2 to compensate (in kg)
  baseCO2 = computed(() => {
    const trip = this.trip();
    if (!trip?.tripImpact) return 0;

    return this.isSingleTraveler()
      ? trip.tripImpact.impactPerTraveller
      : trip.tripImpact.totalImpact;
  });

  // Computed: CO2 to compensate after percentage adjustment (in kg)
  // Use preview response if available, otherwise calculate locally
  co2ToCompensate = computed(() => {
    const preview = this.previewResponse();
    if (preview) {
      return preview.co2Compensated;
    }
    return (this.baseCO2() * this.globalPercentage()) / 100;
  });

  // Computed: CO2 in tonnes
  co2ToCompensateTonnes = computed(() => {
    return this.co2ToCompensate() / 1000;
  });

  // Computed: Contribution amount (EUR) - from preview API or local fallback
  contributionAmount = computed(() => {
    const vatBreakdown = this.vatBreakdown();
    if (vatBreakdown) {
      return vatBreakdown.amountExcludingVat;
    }
    // Fallback to local calculation
    const project = this.selectedProject();
    if (!project) return 0;
    return this.co2ToCompensateTonnes() * project.pricePerTonne;
  });

  // Computed: VAT amount - from preview API or local fallback (21%)
  vatAmount = computed(() => {
    const vatBreakdown = this.vatBreakdown();
    if (vatBreakdown) {
      return vatBreakdown.vatAmount;
    }
    return this.contributionAmount() * 0.21;
  });

  // Computed: Total amount with VAT - from preview API or local fallback
  totalAmount = computed(() => {
    const vatBreakdown = this.vatBreakdown();
    if (vatBreakdown) {
      return vatBreakdown.totalAmount;
    }
    return this.contributionAmount() + this.vatAmount();
  });

  // Validation for each step
  canProceedStep1 = computed(() => this.selectedProject() !== null);
  canProceedStep2 = computed(() => this.globalPercentage() > 0);
  canProceedStep3 = computed(() => this.certificateRecipientName().trim().length > 0);
  canProceedStep4 = computed(() => this.selectedPaymentMethod() !== null);

  /**
   * Initialize wizard with trip data
   */
  initWithTrip(trip: Trip): void {
    this.tripId.set(trip.id);
    this.trip.set(trip);
    this.contributionResult.set(null);

    // Pre-fill VAT number from user's company if available
    const companyVat = this.currentUserService.currentUser()?.company?.vatNumber;
    if (companyVat) {
      this.setVatNumber(companyVat);
    }
  }

  /**
   * Reset wizard state
   */
  reset(): void {
    this.tripId.set('');
    this.trip.set(null);
    this.selectedProject.set(null);
    this.globalPercentage.set(100);
    this.isSingleTraveler.set(false);
    this.certificateRecipientName.set('');
    this.certificateMessage.set('');
    this.contributionResult.set(null);
    this.selectedPaymentMethod.set(null);
    this.bankDetails.set(null);
    // Reset preview state
    this.previewResponse.set(null);
    this.previewLoading.set(false);
    this.previewError.set(null);
    // Reset VAT validation state
    this.vatNumberInput.set('');
    this.vatValidationResult.set(null);
    this.vatValidationLoading.set(false);
  }

  /**
   * Select a project
   */
  selectProject(project: Project): void {
    this.selectedProject.set(project);
  }

  /**
   * Set coverage mode
   */
  setCoverageMode(isSingleTraveler: boolean): void {
    this.isSingleTraveler.set(isSingleTraveler);
  }

  /**
   * Set contribution percentage
   */
  setPercentage(percentage: number): void {
    this.globalPercentage.set(Math.max(0, Math.min(9999, percentage)));
  }

  /**
   * Set certificate recipient name
   */
  setCertificateRecipientName(name: string): void {
    this.certificateRecipientName.set(name);
  }

  /**
   * Set certificate message
   */
  setCertificateMessage(message: string): void {
    this.certificateMessage.set(message.substring(0, 150));
  }

  /**
   * Set payment method
   */
  setPaymentMethod(method: PaymentMethod): void {
    this.selectedPaymentMethod.set(method);
  }

  // ============================================
  // Preview API Methods
  // ============================================

  /**
   * Trigger a preview fetch (debounced).
   * Call this when parameters change to update VAT breakdown.
   */
  triggerPreview(): void {
    this.previewTrigger$.next();
  }

  /**
   * Check if we have all required data to fetch preview
   */
  private canFetchPreview(): boolean {
    return !!(this.tripId() && this.selectedProject() && this.globalPercentage() > 0);
  }

  /**
   * Internal method to fetch preview from API
   */
  private fetchPreviewInternal(): Observable<ContributionPreviewResponse | null> {
    const project = this.selectedProject();
    if (!project) {
      return of(null);
    }

    const dto: PreviewContributionDTO = {
      tripId: this.tripId(),
      isSingleTraveler: this.isSingleTraveler(),
      globalPercentage: this.globalPercentage(),
      projects: [{ projectId: project.id, percentage: 100 }],
    };

    // Include validated VAT number if available
    const vatNumber = this.vatNumberInput();
    const vatValidation = this.vatValidationResult();
    if (vatNumber && vatValidation?.isValid) {
      dto.vatNumber = vatNumber;
    }

    this.previewLoading.set(true);
    this.previewError.set(null);

    return this.http.post<{ data: ContributionPreviewResponse }>(`${this.baseUrl}/preview`, dto).pipe(
      tap((response) => {
        this.previewResponse.set(response.data);
        this.previewLoading.set(false);
      }),
      map((response) => response.data),
      catchError((error) => {
        this.previewError.set(error.message || 'Failed to fetch preview');
        this.previewLoading.set(false);
        return of(null);
      })
    );
  }

  // ============================================
  // VAT Validation Methods
  // ============================================

  /**
   * Set VAT number and trigger validation
   */
  setVatNumber(vatNumber: string): void {
    const cleaned = vatNumber.toUpperCase().replace(/\s/g, '');
    this.vatNumberInput.set(cleaned);

    // Reset validation when input changes
    if (!cleaned) {
      this.vatValidationResult.set(null);
      // Trigger preview without VAT number
      this.triggerPreview();
      return;
    }

    // Trigger debounced validation
    this.vatValidationTrigger$.next(cleaned);
  }

  /**
   * Internal method to validate VAT number via API
   */
  private validateVatInternal(vatNumber: string): Observable<VatValidationResult | null> {
    this.vatValidationLoading.set(true);

    return this.http
      .post<{ data: VatValidationResult }>(`${environment.apiUrl}/vat/validate`, { vatNumber })
      .pipe(
        tap((response) => {
          this.vatValidationResult.set(response.data);
          this.vatValidationLoading.set(false);
          // Trigger preview refresh with new VAT number
          this.triggerPreview();
        }),
        map((response) => response.data),
        catchError(() => {
          this.vatValidationResult.set({
            isValid: false,
            countryCode: null,
            validationMethod: 'invalid',
            isReverseChargeEligible: false,
          });
          this.vatValidationLoading.set(false);
          // Still trigger preview (will use default VAT rate)
          this.triggerPreview();
          return of(null);
        })
      );
  }

  /**
   * Create contribution only (without payment simulation)
   * Returns the created contribution for payment initiation
   */
  createContributionOnly(): Observable<ContributionResult> {
    const project = this.selectedProject();
    if (!project) {
      throw new Error('No project selected');
    }

    const dto: CreateContributionDTO = {
      tripId: this.tripId(),
      isSingleTraveler: this.isSingleTraveler(),
      globalPercentage: this.globalPercentage(),
      projects: [
        {
          projectId: project.id,
          percentage: 100,
        },
      ],
      certificateRecipientName: this.certificateRecipientName(),
      certificateMessage: this.certificateMessage() || undefined,
    };

    return this.http.post<{ data: ContributionResult }>(this.baseUrl, dto).pipe(
      tap((response) => {
        this.contributionResult.set(response.data);
      }),
      map((response) => response.data)
    );
  }

  /**
   * Initiate payment for the current contribution
   * - CARD: Returns checkoutUrl for Stripe redirect
   * - BANK_TRANSFER: Returns bankDetails for display
   */
  initiatePayment(): Observable<PaymentInitiateResponse> {
    const contribution = this.contributionResult();
    const paymentMethod = this.selectedPaymentMethod();

    if (!contribution) {
      throw new Error('No contribution created');
    }
    if (!paymentMethod) {
      throw new Error('No payment method selected');
    }

    return this.http
      .post<{ data: PaymentInitiateResponse }>(
        `${this.baseUrl}/${contribution.id}/payment/initiate`,
        { paymentMethod }
      )
      .pipe(
        tap((response) => {
          // Store bank details if bank transfer
          if (response.data.bankDetails) {
            this.bankDetails.set(response.data.bankDetails);
          }
        }),
        map((response) => response.data)
      );
  }

  /**
   * Verify checkout session after Stripe redirect
   * Sets contributionResult from the response (handles post-redirect state restoration)
   */
  verifyCheckoutSession(
    contributionId: string,
    sessionId: string
  ): Observable<VerifySessionResponse> {
    return this.http
      .get<{ data: VerifySessionResponse }>(
        `${this.baseUrl}/${contributionId}/payment/verify-session`,
        { params: { session_id: sessionId } }
      )
      .pipe(
        tap((response) => {
          // Use full contribution from response if available (after Stripe redirect)
          if (response.data.contribution) {
            this.contributionResult.set(response.data.contribution);
          } else {
            // Fallback: Update existing contribution result with payment status
            const current = this.contributionResult();
            if (current) {
              this.contributionResult.set({
                ...current,
                status: response.data.status,
                certificateNumber: response.data.certificateNumber,
              });
            }
          }

          // Set project from response (for success screen display after redirect)
          if (response.data.project) {
            // Cast to Project type - only necessary fields for display are present
            this.selectedProject.set(response.data.project as unknown as Project);
          }
        }),
        map((response) => response.data)
      );
  }

  /**
   * Get the certificate URL from the result
   */
  getCertificateUrl(): string | null {
    return this.contributionResult()?.certificateUrl || null;
  }

  /**
   * Check payment status for a contribution.
   * Used to verify payment completion when returning from Stripe (multi-window scenario).
   */
  checkPaymentStatus(contributionId: string): Observable<{ status: string }> {
    return this.http
      .get<{ data: { contribution: { status: string } } }>(
        `${this.baseUrl}/${contributionId}/payment/status`
      )
      .pipe(map((response) => ({ status: response.data.contribution.status })));
  }

  // ============================================
  // URL State Persistence
  // ============================================

  /**
   * Export current wizard state as URL query params.
   * Only includes non-default values to keep URL clean.
   */
  getStateAsParams(): Record<string, string | undefined> {
    const params: Record<string, string | undefined> = {};

    const projectId = this.selectedProject()?.id;
    if (projectId) params['projectId'] = projectId;

    if (this.isSingleTraveler()) params['single'] = 'true';

    const pct = this.globalPercentage();
    if (pct !== 100) params['pct'] = String(pct);

    const name = this.certificateRecipientName();
    if (name) params['name'] = name;

    const msg = this.certificateMessage();
    if (msg) params['msg'] = msg;

    return params;
  }

  /**
   * Restore wizard state from URL query params.
   * Returns true if restoration was successful.
   */
  restoreFromParams(params: WizardUrlState, projects: Project[]): boolean {
    // Restore project selection
    if (params.projectId) {
      const project = projects.find((p) => p.id === params.projectId);
      if (project) {
        this.selectedProject.set(project);
      } else {
        return false; // Project not found - invalid state
      }
    }

    // Restore contribution settings
    if (params.single === 'true') {
      this.isSingleTraveler.set(true);
    }

    if (params.pct) {
      const pct = parseInt(params.pct, 10);
      if (!isNaN(pct) && pct > 0 && pct <= 9999) {
        this.globalPercentage.set(pct);
      }
    }

    // Restore certificate details
    if (params.name) {
      this.certificateRecipientName.set(params.name);
    }

    if (params.msg) {
      this.certificateMessage.set(params.msg);
    }

    return true;
  }

  /**
   * Check if the wizard can access a given step based on current state.
   * Used to prevent users from jumping to steps with missing required data.
   */
  canAccessStep(step: number): boolean {
    if (step <= 1) return true;
    if (step === 2) return this.canProceedStep1();
    if (step === 3) return this.canProceedStep1() && this.canProceedStep2();
    if (step === 4) return this.canProceedStep1() && this.canProceedStep2() && this.canProceedStep3();
    return false;
  }
}
